import { DataLayerErrorType } from '../types/enums';
import { DataLayerService } from '../services/data-layer-service';
import { LocalizationService } from '../services/localization-service';
import { DialogCreators } from './dialog-creators';
import { ItemPricingUnavailableError } from '../online-ordering/errors/item-pricing-unavailable-error';

export class ErrorListener {
    private readonly _dataLayer: DataLayerService;
    private readonly _localizationService: LocalizationService;

    constructor(dataLayer: DataLayerService, localizationService: LocalizationService) {
        this._dataLayer = dataLayer;
        this._localizationService = localizationService;
    }

    public init(): void {
        this._setupGlobalErrorHandler();
        this._setupConsoleErrorListeners();
        this._setupUnhandledRejectionListener();
    }

    // Backstop for any pricing path without its own try/catch -- ADO Bug 86902.
    private _setupUnhandledRejectionListener(): void {
        window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
            if (!(event.reason instanceof ItemPricingUnavailableError))
                return;

            event.preventDefault();
            this._dataLayer.error_message(event.reason.message, DataLayerErrorType.USER_ERROR);
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("ITEM_PRICING_UNAVAILABLE"),
                false
            );
        });
    }

    private _setupGlobalErrorHandler(): void {
        window.onerror = (
            message: string | Event,
        ): boolean => {
            if (this._dataLayer) {
                const formattedMessage = typeof message === 'string' ? message : 'Unknown script error';
                this._dataLayer.error_message(formattedMessage, DataLayerErrorType.CONSOLE_ERROR);
            }

            return false; // allow default logging
        };
    }

    private _setupConsoleErrorListeners(): void {
        const originalConsoleError = console.error;

        console.error = (...args: any[]) => {
            if (originalConsoleError)
                originalConsoleError.apply(console, args);

            const formattedMessage = args
                .map(arg =>
                    typeof arg === 'string'
                        ? arg
                        : arg instanceof Error
                            ? arg.message
                            : JSON.stringify(arg)
                )
                .join(' | ');

            if (this._dataLayer)
                this._dataLayer.error_message(formattedMessage, DataLayerErrorType.CONSOLE_ERROR);
        };
    }
}